import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { JOURNAL_PROMPTS, MOOD_OPTIONS } from "@/data/journal";
import { CustomEmotions } from "@/components/CustomEmotions";
import { useCustomEmotions } from "@/hooks/useCustomEmotions";
import { logError } from "@/lib/log";
import { JournalMic } from "@/components/journal/JournalMic";

const PromptBox = ({ prompt, setPrompt }) => {
  const [busy, setBusy] = useState(false);
  const [smart, setSmart] = useState([]);

  useEffect(() => {
    api.get("/journaling/prompts").then((r) => setSmart(r.data.prompts || [])).catch((e) => logError("Smart prompts failed:", e));
  }, []);

  const aiPrompt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/journaling/ai-prompt");
      if (data.prompt || data.prompts?.[0]) setPrompt(data.prompt || data.prompts[0]);
    } catch (e) {
      toast.error(errMsg(e, "Could not generate a prompt"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="mm-soft p-4 flex justify-between gap-3 items-start">
        <p className="font-display text-lg" data-testid="journal-prompt">{prompt}</p>
        <button className="mm-btn-ghost !px-3 !py-2 shrink-0 bg-mm-surface" onClick={aiPrompt} disabled={busy} data-testid="journal-ai-prompt-button">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}<span className="hidden sm:inline">AI prompt</span>
        </button>
      </div>
      {smart.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {smart.map((s, i) => <button key={s} className="mm-chip !text-xs" onClick={() => setPrompt(s)} data-testid={`journal-smart-prompt-${i}`}>{s}</button>)}
        </div>
      )}
    </>
  );
};

export const JournalComposer = ({ category, onSave, initialPrompt }) => {
  const [prompt, setPrompt] = useState(initialPrompt || JOURNAL_PROMPTS.gratitude[0]);
  const lastCat = useRef(initialPrompt ? category : null);
  const [text, setText] = useState("");
  const cursor = useRef(null);
  const [mood, setMood] = useState(null);
  const custom = useCustomEmotions();
  const tag = (e) => `${e.emoji} ${e.label}`;

  useEffect(() => {
    if (lastCat.current === category) return;
    lastCat.current = category;
    const list = JOURNAL_PROMPTS[category] || [];
    if (list.length) setPrompt(list[Math.floor(Math.random() * list.length)]);
  }, [category]);

  const insertSpoken = (spoken) => {
    const pos = cursor.current;
    cursor.current = null;
    setText((prev) => {
      const at = Math.min(pos ?? prev.length, prev.length);
      const before = prev.slice(0, at), after = prev.slice(at);
      return `${before}${before && !/\s$/.test(before) ? " " : ""}${spoken}${after && !/^\s/.test(after) ? " " : ""}${after}`;
    });
    toast.success("Added to your entry. Edit anything before saving.");
  };

  const save = async () => {
    if (!text.trim()) return toast.error("Write something first");
    try {
      await onSave({ prompt, text: text.trim(), mood, category });
      setText("");
      setMood(null);
      toast.success("Journal entry saved ✏️");
    } catch (e) {
      toast.error(errMsg(e, "Could not save entry"));
    }
  };

  return (
    <div className="mm-card p-6">
      <PromptBox prompt={prompt} setPrompt={setPrompt} />
      <textarea onBlur={(e) => { cursor.current = e.target.selectionStart; }} className="mm-input min-h-[200px] mt-4" placeholder="Start writing, or tap Speak…" value={text} onChange={(e) => setText(e.target.value)} data-testid="journal-text-input" />
      <div className="flex items-center justify-between gap-3 mt-2">
        <JournalMic onText={insertSpoken} />
        <span className="text-xs text-mm-muted">Up to 3 minutes. Only the words are kept.</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        {MOOD_OPTIONS.map((m) => (
          <button key={m.label} onClick={() => setMood(m.label === mood ? null : m.label)} className={`mm-chip ${mood === m.label ? "mm-chip-on" : ""}`} data-testid={`journal-mood-${m.label.toLowerCase()}`}>{m.emoji} {m.label}</button>
        ))}
        <CustomEmotions emotions={custom.items} isOn={(e) => mood === tag(e)} onToggle={(e) => setMood(mood === tag(e) ? null : tag(e))}
          onAdd={custom.add} onRemove={(id) => { const e = custom.items.find((x) => x.id === id); if (e && mood === tag(e)) setMood(null); custom.remove(id); }} />
      </div>
      <button className="mm-btn mt-5" onClick={save} data-testid="journal-save-button">Save entry</button>
    </div>
  );
};
